import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Archive, BadgePercent, CreditCard, Loader2, Pencil, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/api/client'
import { formatCurrency, formatDateHuman } from '@/lib/utils'
import type { APIResponse, CardPromo } from '@/types'
import { MarketingShell } from './MarketingShell'
import { CardPromoDrawer } from './CardPromoDrawer'

/** Display labels in Mon-first order; values follow Go time.Weekday (0=Sun). */
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatDays(days: number[]): string {
  if (!days || days.length === 0 || days.length === 7) return 'Every day'
  // Mon-first presentation.
  const order = [1, 2, 3, 4, 5, 6, 0]
  return order
    .filter((d) => days.includes(d))
    .map((d) => DAY_LABELS[d])
    .join(', ')
}

function formatValidity(p: CardPromo): string {
  if (!p.valid_from && !p.valid_to) return 'Always active'
  return `${formatDateHuman(p.valid_from)} → ${formatDateHuman(p.valid_to)}`
}

function formatTerms(p: CardPromo): string {
  const parts = [`${String(Number(p.discount_percent.toFixed(2)))}% off`]
  if (p.max_discount_amount != null) parts.push(`up to ${formatCurrency(p.max_discount_amount)}`)
  if (p.min_bill_amount != null && p.min_bill_amount > 0)
    parts.push(`min bill ${formatCurrency(p.min_bill_amount)}`)
  return parts.join(' · ')
}

/**
 * Bank card promo catalog — rotating "X% off when paying with <bank card>"
 * campaigns. The bank reimburses the restaurant per redemption, so each
 * row also shows the amount to claim back; the order-level claim report
 * lives in Reports → Card Promos.
 *
 * Managed by admin AND manager (backend group adminManagerWrite) — promos
 * rotate every few months and the floor manager usually holds the bank's
 * campaign letter. Manager is the ONLY non-admin role inside the Marketing
 * Suite, which is why MarketingShell filters its tab row by role.
 */
export function AdminCardPromos() {
  const [includeArchived, setIncludeArchived] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | null>(null)
  const [editing, setEditing] = useState<CardPromo | null>(null)

  const listQuery = useQuery<APIResponse<CardPromo[]>>({
    queryKey: ['card-promos', { includeArchived }],
    queryFn: () => apiClient.listCardPromos(includeArchived),
    staleTime: 30_000,
  })

  const promos = listQuery.data?.data ?? []
  const { active, archived } = useMemo(() => {
    const a: CardPromo[] = []
    const r: CardPromo[] = []
    for (const p of promos) (p.is_active ? a : r).push(p)
    return { active: a, archived: r }
  }, [promos])

  function openEdit(p: CardPromo) {
    setEditing(p)
    setDrawerMode('edit')
  }

  const totallyEmpty = !listQuery.isLoading && active.length === 0 && archived.length === 0

  return (
    <MarketingShell
      title="Bank card promos"
      description={
        'Percentage discounts for paying with specific bank cards ("20% off with HBL Platinum"). ' +
        'The cashier picks the matching promo after selecting Card at checkout; the bank ' +
        'reimburses you per redemption — claim totals are in Reports.'
      }
    >
      <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          size="sm"
          onClick={() => {
            setEditing(null)
            setDrawerMode('create')
          }}
          className="gap-1.5"
        >
          <Plus className="h-4 w-4" />
          New card promo
        </Button>
        <label
          htmlFor="cp-show-archived"
          className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <Switch
            id="cp-show-archived"
            checked={includeArchived}
            onCheckedChange={setIncludeArchived}
            className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 data-[state=checked]:[&>span]:translate-x-3"
          />
          <span>Show archived</span>
        </label>
      </div>

      {totallyEmpty && (
        <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 p-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <BadgePercent className="h-6 w-6" aria-hidden />
          </div>
          <h3 className="text-base font-semibold text-foreground">No bank card promos yet</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            When a bank runs a discount campaign with you ("20% off with HBL cards, Mondays"),
            add it here — the counter shows it as a one-tap chip whenever Card is the payment
            method and the promo's day/date/minimum conditions are met.
          </p>
          <div className="mt-6">
            <Button
              onClick={() => {
                setEditing(null)
                setDrawerMode('create')
              }}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add your first card promo
            </Button>
          </div>
        </div>
      )}

      {!totallyEmpty && (
        <PromoTable
          title="Active promos"
          icon={CreditCard}
          promos={active}
          loading={listQuery.isLoading}
          onEdit={openEdit}
          emptyState="No active card promos. Add one when a bank campaign starts."
        />
      )}

      {includeArchived && archived.length > 0 && (
        <PromoTable
          title="Archived"
          icon={Archive}
          promos={archived}
          loading={false}
          onEdit={openEdit}
          emptyState=""
          archivedTone
        />
      )}

      <CardPromoDrawer
        mode={drawerMode ?? 'create'}
        initial={editing}
        open={drawerMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDrawerMode(null)
            setEditing(null)
          }
        }}
      />
      </div>
    </MarketingShell>
  )
}

function PromoTable({
  title,
  icon: Icon,
  promos,
  loading,
  onEdit,
  emptyState,
  archivedTone,
}: {
  title: string
  icon: typeof CreditCard
  promos: CardPromo[]
  loading: boolean
  onEdit: (p: CardPromo) => void
  emptyState: string
  archivedTone?: boolean
}) {
  return (
    <div className={`rounded-lg border border-border/80 ${archivedTone ? 'bg-muted/30' : ''}`}>
      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/40 px-4 py-2.5">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <Badge variant="outline" className="ml-1 text-sm tabular-nums">
          {promos.length}
        </Badge>
      </div>
      {loading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : promos.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">{emptyState}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableHead className="w-[12%]">Bank</TableHead>
              <TableHead className="w-[22%]">Card</TableHead>
              <TableHead>Terms</TableHead>
              <TableHead className="hidden w-[16%] md:table-cell">Days</TableHead>
              <TableHead className="hidden w-[16%] lg:table-cell">Validity</TableHead>
              <TableHead className="w-[10%] text-right">Uses</TableHead>
              <TableHead className="hidden w-[12%] text-right sm:table-cell">To claim</TableHead>
              <TableHead className="w-[6%]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {promos.map((p) => (
              <TableRow key={p.id} className={archivedTone ? 'opacity-70' : ''}>
                <TableCell className="text-sm font-semibold">{p.bank_name}</TableCell>
                <TableCell className="text-sm">{p.card_label}</TableCell>
                <TableCell className="text-sm">{formatTerms(p)}</TableCell>
                <TableCell className="hidden text-sm md:table-cell">{formatDays(p.days_of_week)}</TableCell>
                <TableCell className="hidden text-sm lg:table-cell">{formatValidity(p)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {p.redemption_count.toLocaleString()}
                </TableCell>
                <TableCell className="hidden text-right text-sm tabular-nums sm:table-cell">
                  {formatCurrency(p.discount_attributed_pkr)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(p)}
                    className="h-7 gap-1.5 text-sm"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
